/**
 * Paste a run sheet (#2655): two calls. `preview` reads pasted text into rows
 * and writes nothing. `create` makes the confirmed rows as tasks under a date
 * node, then refreshes the task list.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import { TASK_NODES_KEY } from "./taskNodes";

export type RunSheetRow = components["schemas"]["RunSheetRow"];
export type RunSheetCreated = components["schemas"]["RunSheetCreated"];

/** A row the lead can correct before the tasks are made. */
export interface RunSheetDraftRow {
  time: string | null;
  title: string;
  place: string | null;
  holderName: string | null;
  holderMembershipId: string | null;
  durationMinutes: number | null;
  warnings: string[];
}

export function toDraftRow(row: RunSheetRow): RunSheetDraftRow {
  return {
    time: row.time,
    title: row.title,
    place: row.place,
    holderName: row.holder_name,
    holderMembershipId: row.holder_membership_id,
    durationMinutes: row.duration_minutes ?? null,
    warnings: row.warnings,
  };
}

function base(teamId: string): string {
  return `/api/teams/${teamId}/tasks/run-sheet`;
}

export function useRunSheetPreview(teamId: string | undefined) {
  return useMutation({
    mutationFn: ({ text }: { text: string }) =>
      apiFetch<{ rows: RunSheetRow[] }>(`${base(teamId as string)}/preview`, {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
  });
}

export function useCreateRunSheet(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, rows }: { date: string; rows: RunSheetDraftRow[] }) =>
      apiFetch<RunSheetCreated>(base(teamId as string), {
        method: "POST",
        body: JSON.stringify({
          date,
          rows: rows.map((r) => ({
            time: r.time || null,
            title: r.title.trim(),
            place: r.place?.trim() || null,
            holder_membership_id: r.holderMembershipId,
            duration_minutes: r.durationMinutes,
          })),
        }),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: TASK_NODES_KEY(teamId ?? "") }),
  });
}

/** Archive the tasks one paste made, in one request (#2718). */
export function useArchiveManyTaskNodes(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeIds }: { nodeIds: string[] }) =>
      apiFetch<{ archived_ids: string[] }>(
        `/api/teams/${teamId as string}/tasks/nodes/archive-many`,
        { method: "POST", body: JSON.stringify({ node_ids: nodeIds }) },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: TASK_NODES_KEY(teamId ?? "") }),
  });
}
